import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
  type ReactNode,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ChangeEvent,
} from 'react';
import { Document, pdfjs } from 'react-pdf';
import {
  Loader2,
  ZoomIn,
  ZoomOut,
  Maximize,
  MoveHorizontal,
  MoveVertical,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const ZOOM_PRESETS = [25, 50, 75, 100, 125, 150, 200, 300] as const;

// Preserved as a runtime object with string-literal values. Shape MUST stay
// identical — FilePreviewModal and other consumers import { FIT_MODES } by name
// and compare against its property values.
export const FIT_MODES = {
  WIDTH: 'fit-width',
  HEIGHT: 'fit-height',
  PAGE: 'fit-page',
} as const;

export type FitMode = (typeof FIT_MODES)[keyof typeof FIT_MODES];

const CONTAINER_PADDING = 24;
const PAGE_GAP = 16;
const BUFFER = 2;
// Render at this multiple of fit-page zoom so CSS scale covers most zoom levels without re-render
const BASE_SCALE_MULTIPLIER = 2;

// ---------- Companion types (file-local per B.12b coupled-pair precedent) ----------

// Minimal shape of the pdfjs page proxy used by this file.
interface PdfPageProxyLike {
  getViewport: (options: { scale: number }) => { width: number; height: number };
  render: (options: {
    canvasContext: CanvasRenderingContext2D;
    viewport: { width: number; height: number };
  }) => { promise: Promise<void>; cancel: () => void };
}

interface PdfDocumentProxyLike {
  numPages: number;
  getPage: (pageNumber: number) => Promise<PdfPageProxyLike>;
}

interface RenderTaskLike {
  promise: Promise<void>;
  cancel: () => void;
}

interface PageSize {
  width: number;
  height: number;
}

interface ContainerSize {
  width: number;
  height: number;
}

interface PdfViewerState {
  zoom: number;
  pageNumber: number;
  numPages: number | null;
  fitMode: FitMode | null;
}

type ZoomUpdater = number | ((prev: number) => number);

export interface PdfViewerHandle {
  zoom: number;
  pageNumber: number;
  numPages: number | null;
  fitMode: FitMode | null;
  setZoom: (updater: ZoomUpdater) => void;
  goToPage: (page: number) => void;
  setFitMode: (mode: FitMode | null) => void;
}

// All-optional superset — consumers (FilePreviewModal, PropostaPDFGenerator,
// PDFPreview) supply a subset of these props. Keep every field optional so
// boundary casts in zeroed consumers remain structurally compatible.
export interface PdfViewerProps {
  file?: Blob | string | null;
  maxHeight?: number | string;
  showZoom?: boolean;
  showPageIndicator?: boolean;
  onPageChange?: (page: number, numPages: number) => void;
  headerExtra?: ReactNode;
  className?: string;
  defaultFitMode?: FitMode;
  transparent?: boolean;
  onStateChange?: (state: PdfViewerState) => void;
}

interface CanvasPageProps {
  pdfDoc: PdfDocumentProxyLike | null;
  pageNumber: number;
  baseScale: number;
  zoom: number;
  width: number;
  height: number;
}

/**
 * Single page rendered directly via pdfjs-dist canvas API.
 * Renders once at baseScale; zoom is pure CSS transform.
 */
function CanvasPage({ pdfDoc, pageNumber, baseScale, zoom, width, height }: CanvasPageProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const renderTaskRef = useRef<RenderTaskLike | null>(null);
  const renderedScaleRef = useRef<number | null>(null);
  const dpr = window.devicePixelRatio || 2;

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;
    // Skip if already rendered at this baseScale
    if (renderedScaleRef.current === baseScale) return;

    let cancelled = false;

    const render = async () => {
      try {
        // Cancel any in-flight render
        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
          renderTaskRef.current = null;
        }

        const page = await pdfDoc.getPage(pageNumber);
        if (cancelled) return;

        const viewport = page.getViewport({ scale: baseScale * dpr });
        const canvas = canvasRef.current;
        if (!canvas) return;

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        const ctx = canvas.getContext('2d', { alpha: false });
        if (!ctx) return;
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, viewport.width, viewport.height);

        const task = page.render({ canvasContext: ctx, viewport });
        renderTaskRef.current = task;

        await task.promise;
        if (!cancelled) {
          renderedScaleRef.current = baseScale;
        }
      } catch (err) {
        // pdfjs throws RenderingCancelledException on cancel — ignore it.
        const name =
          err && typeof err === 'object' && 'name' in err
            ? (err as { name?: unknown }).name
            : undefined;
        if (name !== 'RenderingCancelledException') {
          console.error('Erro ao renderizar página', pageNumber, err);
        }
      }
    };

    render();

    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pdfDoc, pageNumber, baseScale, dpr]);

  // CSS scale: target zoom / rendered base scale
  const cssScale = baseScale > 0 ? zoom / baseScale : 1;
  // Canvas CSS dimensions (at baseScale, before CSS zoom)
  const canvasCssWidth = width * (baseScale / zoom);
  const canvasCssHeight = height * (baseScale / zoom);

  return (
    <div
      style={{
        width,
        height,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: canvasCssWidth,
          height: canvasCssHeight,
          transform: `scale(${cssScale})`,
          transformOrigin: 'top center',
        }}
      />
    </div>
  );
}

/**
 * PDF viewer with Google Drive-like smooth zoom experience.
 * Uses pdfjs-dist directly for canvas rendering + CSS transform for zoom.
 */
const PdfViewer = forwardRef<PdfViewerHandle, PdfViewerProps>(function PdfViewer(
  {
    file,
    maxHeight,
    showZoom = true,
    showPageIndicator = true,
    onPageChange,
    headerExtra,
    className,
    defaultFitMode = FIT_MODES.PAGE,
    transparent = false,
    onStateChange,
  },
  ref,
) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [zoom, setZoom] = useState<number>(1);
  const [fitMode, setFitMode] = useState<FitMode | null>(defaultFitMode);
  const [pdfPageSize, setPdfPageSize] = useState<PageSize | null>(null);
  const [containerSize, setContainerSize] = useState<ContainerSize>({ width: 0, height: 0 });
  const [visiblePages, setVisiblePages] = useState<Set<number>>(new Set<number>([0, 1, 2]));
  const [baseScale, setBaseScale] = useState<number>(1);
  const [pdfDoc, setPdfDoc] = useState<PdfDocumentProxyLike | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const slotRefs = useRef<Array<HTMLDivElement | null>>([]);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const prevZoomRef = useRef<number>(1);
  const fitZoomRef = useRef<number>(1); // Store the fit-page zoom to calculate baseScale

  // Measure container
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !file) return;
    const measure = () => {
      setContainerSize({
        width: el.clientWidth - CONTAINER_PADDING,
        height: el.clientHeight - CONTAINER_PADDING,
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [file]);

  // Calculate zoom from fit mode
  useEffect(() => {
    if (!fitMode || !pdfPageSize || !containerSize.width || !containerSize.height) return;
    const { width: pw, height: ph } = pdfPageSize;
    const { width: cw, height: ch } = containerSize;

    let newZoom = 1;
    if (fitMode === FIT_MODES.WIDTH) {
      newZoom = cw / pw;
    } else if (fitMode === FIT_MODES.HEIGHT) {
      newZoom = ch / ph;
    } else if (fitMode === FIT_MODES.PAGE) {
      newZoom = Math.min(cw / pw, ch / ph);
    }
    newZoom = Math.max(0.1, Math.min(5, newZoom));
    const rounded = Math.round(newZoom * 100) / 100;
    setZoom(rounded);
    fitZoomRef.current = rounded;
    // Base scale = multiplier * fit zoom (render at higher res for CSS zoom headroom)
    setBaseScale(Math.round(rounded * BASE_SCALE_MULTIPLIER * 100) / 100);
  }, [fitMode, pdfPageSize, containerSize]);

  // Update baseScale when manual zoom exceeds current headroom
  useEffect(() => {
    if (zoom > baseScale * 0.95) {
      // User zoomed near or beyond base scale, re-render at higher res
      setBaseScale(Math.round(zoom * BASE_SCALE_MULTIPLIER * 100) / 100);
    }
  }, [zoom, baseScale]);

  // Preserve scroll position when zoom changes
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !pdfPageSize || prevZoomRef.current === zoom) return;
    const prevZoom = prevZoomRef.current;
    const ratio = zoom / prevZoom;
    const scrollCenter = el.scrollTop + el.clientHeight / 2;
    el.scrollTop = scrollCenter * ratio - el.clientHeight / 2;
    prevZoomRef.current = zoom;
  }, [zoom, pdfPageSize]);

  // Target dimensions
  const targetWidth = pdfPageSize ? Math.round(pdfPageSize.width * zoom) : null;
  const targetHeight = pdfPageSize ? Math.round(pdfPageSize.height * zoom) : null;

  // IntersectionObserver for virtualization
  useEffect(() => {
    if (!numPages || !targetHeight) return;
    if (observerRef.current) observerRef.current.disconnect();

    const rootEl = containerRef.current;
    if (!rootEl) return;

    const margin = targetHeight * BUFFER + 'px';
    const observer = new IntersectionObserver(
      (entries) => {
        setVisiblePages((prev) => {
          const next = new Set<number>(prev);
          entries.forEach((entry) => {
            const target = entry.target as HTMLElement;
            const idx = Number(target.dataset.pageIndex);
            if (entry.isIntersecting) {
              next.add(idx);
            } else {
              next.delete(idx);
            }
          });
          if (next.size === prev.size && [...next].every((v) => prev.has(v))) return prev;
          return next;
        });
      },
      { root: rootEl, rootMargin: `${margin} 0px ${margin} 0px`, threshold: 0 },
    );

    observerRef.current = observer;
    slotRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [numPages, targetHeight]);

  // Track current page from scroll
  const handleScroll = useCallback(() => {
    if (!containerRef.current || !targetHeight || !numPages) return;
    const container = containerRef.current;
    const scrollCenter = container.scrollTop + container.clientHeight / 2;
    const pageWithGap = targetHeight + PAGE_GAP;
    const current = Math.min(numPages, Math.max(1, Math.floor(scrollCenter / pageWithGap) + 1));
    if (current !== pageNumber) {
      setPageNumber(current);
      onPageChange?.(current, numPages);
    }
  }, [targetHeight, numPages, pageNumber, onPageChange]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Reset state when file changes
  useEffect(() => {
    setNumPages(null);
    setPageNumber(1);
    setZoom(1);
    setBaseScale(1);
    prevZoomRef.current = 1;
    setFitMode(defaultFitMode);
    setPdfPageSize(null);
    setPdfDoc(null);
    setVisiblePages(new Set<number>([0, 1, 2]));
    slotRefs.current = [];
  }, [file, defaultFitMode]);

  // Navigate to a specific page by scrolling
  const goToPage = useCallback(
    (page: number) => {
      const el = containerRef.current;
      if (!el || !targetHeight || !numPages) return;
      const target = Math.max(1, Math.min(numPages, page));
      const pageWithGap = targetHeight + PAGE_GAP;
      el.scrollTop = (target - 1) * pageWithGap;
      setPageNumber(target);
      onPageChange?.(target, numPages);
    },
    [targetHeight, numPages, onPageChange],
  );

  const handleManualZoom = useCallback((newZoomFn: ZoomUpdater) => {
    setFitMode(null);
    setZoom((z) => {
      const val = typeof newZoomFn === 'function' ? newZoomFn(z) : newZoomFn;
      return Math.max(0.25, Math.min(3, Math.round(val * 100) / 100));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Expose state to parent for external controls
  useImperativeHandle(
    ref,
    () => ({
      zoom,
      pageNumber,
      numPages,
      fitMode,
      setZoom: handleManualZoom,
      goToPage,
      setFitMode,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [zoom, pageNumber, numPages, fitMode, handleManualZoom, goToPage],
  );

  // Notify parent of state changes
  useEffect(() => {
    onStateChange?.({ zoom, pageNumber, numPages, fitMode });
  }, [zoom, pageNumber, numPages, fitMode, onStateChange]);

  const btnColor = transparent ? 'white' : '#374151';
  const btnBorder = transparent ? 'rgba(255,255,255,0.3)' : '#d1d5db';
  const inputBorder = transparent ? 'rgba(255,255,255,0.3)' : '#d1d5db';
  const inputColor = transparent ? 'white' : '#374151';
  const inputBg = transparent ? 'rgba(255,255,255,0.1)' : 'none';

  const fitBtnStyleT = (active: boolean): CSSProperties => ({
    background: active ? (transparent ? 'rgba(255,255,255,0.2)' : '#e0e7ff') : 'none',
    border: active
      ? `1px solid ${transparent ? 'rgba(255,255,255,0.5)' : '#818cf8'}`
      : `1px solid ${btnBorder}`,
    borderRadius: '4px',
    width: '28px',
    height: '28px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  // Build controls JSX
  const controlsNode: ReactNode =
    showZoom || showPageIndicator ? (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        {showPageIndicator && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <button
              onClick={() => goToPage(pageNumber - 1)}
              disabled={pageNumber <= 1}
              title="Página anterior"
              style={{
                background: 'none',
                border: `1px solid ${btnBorder}`,
                borderRadius: '4px',
                width: '28px',
                height: '28px',
                cursor: pageNumber <= 1 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: pageNumber <= 1 ? 0.4 : 1,
              }}
            >
              <ChevronLeft size={14} color={btnColor} />
            </button>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '13px',
                color: inputColor,
              }}
            >
              <input
                type="text"
                value={pageNumber}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val)) goToPage(val);
                }}
                onKeyDown={(e: ReactKeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                }}
                style={{
                  width: '40px',
                  textAlign: 'center',
                  border: `1px solid ${inputBorder}`,
                  borderRadius: '4px',
                  padding: '3px 4px',
                  fontSize: '13px',
                  color: inputColor,
                  background: inputBg,
                  outline: 'none',
                }}
              />
              <span>de {numPages || '...'}</span>
            </div>
            <button
              onClick={() => goToPage(pageNumber + 1)}
              disabled={!numPages || pageNumber >= numPages}
              title="Próxima página"
              style={{
                background: 'none',
                border: `1px solid ${btnBorder}`,
                borderRadius: '4px',
                width: '28px',
                height: '28px',
                cursor: !numPages || pageNumber >= numPages ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: !numPages || pageNumber >= numPages ? 0.4 : 1,
              }}
            >
              <ChevronRight size={14} color={btnColor} />
            </button>
          </div>
        )}
        {showZoom && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
              <button
                onClick={() => setFitMode(FIT_MODES.WIDTH)}
                title="Caber na largura"
                style={fitBtnStyleT(fitMode === FIT_MODES.WIDTH)}
              >
                <MoveHorizontal
                  size={14}
                  color={
                    fitMode === FIT_MODES.WIDTH ? (transparent ? 'white' : '#4f46e5') : btnColor
                  }
                />
              </button>
              <button
                onClick={() => setFitMode(FIT_MODES.HEIGHT)}
                title="Caber na altura"
                style={fitBtnStyleT(fitMode === FIT_MODES.HEIGHT)}
              >
                <MoveVertical
                  size={14}
                  color={
                    fitMode === FIT_MODES.HEIGHT ? (transparent ? 'white' : '#4f46e5') : btnColor
                  }
                />
              </button>
              <button
                onClick={() => setFitMode(FIT_MODES.PAGE)}
                title="Caber inteiro"
                style={fitBtnStyleT(fitMode === FIT_MODES.PAGE)}
              >
                <Maximize
                  size={14}
                  color={
                    fitMode === FIT_MODES.PAGE ? (transparent ? 'white' : '#4f46e5') : btnColor
                  }
                />
              </button>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                borderLeft: `1px solid ${btnBorder}`,
                paddingLeft: '10px',
              }}
            >
              <button
                onClick={() => handleManualZoom((z) => z - 0.25)}
                disabled={zoom <= 0.25}
                style={{
                  background: 'none',
                  border: `1px solid ${btnBorder}`,
                  borderRadius: '4px',
                  width: '28px',
                  height: '28px',
                  cursor: zoom <= 0.25 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: zoom <= 0.25 ? 0.4 : 1,
                }}
              >
                <ZoomOut size={14} color={btnColor} />
              </button>
              <select
                value=""
                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  const val = parseFloat(e.target.value);
                  if (!isNaN(val)) handleManualZoom(() => val);
                }}
                style={{
                  appearance: 'none',
                  background: inputBg,
                  border: `1px solid ${btnBorder}`,
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: inputColor,
                  fontWeight: 500,
                  padding: '4px 8px',
                  minWidth: '60px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='${encodeURIComponent(btnColor)}' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 4px center',
                  paddingRight: '18px',
                }}
              >
                <option value="" disabled hidden>
                  {Math.round(zoom * 100)}%
                </option>
                {ZOOM_PRESETS.map((pct) => (
                  <option key={pct} value={pct / 100}>
                    {pct}%
                  </option>
                ))}
              </select>
              <button
                onClick={() => handleManualZoom((z) => z + 0.25)}
                disabled={zoom >= 3}
                style={{
                  background: 'none',
                  border: `1px solid ${btnBorder}`,
                  borderRadius: '4px',
                  width: '28px',
                  height: '28px',
                  cursor: zoom >= 3 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: zoom >= 3 ? 0.4 : 1,
                }}
              >
                <ZoomIn size={14} color={btnColor} />
              </button>
            </div>
          </>
        )}
      </div>
    ) : null;

  const showInternalHeader = Boolean(controlsNode) || Boolean(headerExtra);

  return (
    <div
      className={className}
      style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
    >
      {showInternalHeader && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            background: transparent ? 'rgba(0,0,0,0.4)' : 'white',
            borderRadius: transparent ? 0 : '8px',
            boxShadow: transparent ? 'none' : '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: transparent ? 0 : '12px',
            flexWrap: 'wrap',
            gap: '8px',
          }}
        >
          {controlsNode}
          {headerExtra}
        </div>
      )}

      <div
        ref={containerRef}
        className={transparent ? 'pdf-scroll-dark' : ''}
        style={{
          flex: 1,
          overflow: 'auto',
          background: transparent ? 'transparent' : '#6b7280',
          borderRadius: '8px',
          padding: '12px',
          ...(maxHeight ? { maxHeight } : {}),
        }}
      >
        <Document
          file={file ?? undefined}
          onLoadSuccess={async (pdfDocument) => {
            // react-pdf's PDFDocumentProxy is structurally compatible with our
            // file-local PdfDocumentProxyLike (numPages + getPage). Narrow via
            // two-step unknown cast at the boundary.
            const doc = pdfDocument as unknown as PdfDocumentProxyLike;
            setPdfDoc(doc);
            setNumPages(doc.numPages);
            setPageNumber(1);
            slotRefs.current = new Array(doc.numPages).fill(null);
            onPageChange?.(1, doc.numPages);
            const page = await doc.getPage(1);
            const vp = page.getViewport({ scale: 1 });
            setPdfPageSize({ width: vp.width, height: vp.height });
          }}
          onLoadError={(error) => console.error('Erro ao carregar PDF:', error)}
          loading={
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                color: 'white',
                padding: '40px',
              }}
            >
              <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
              Carregando PDF...
            </div>
          }
        >
          {numPages && targetWidth && targetHeight && pdfDoc && (
            <div
              style={{
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: PAGE_GAP + 'px',
                minWidth: '100%',
              }}
            >
              {Array.from({ length: numPages }, (_, index) => (
                <div
                  key={`slot_${index}`}
                  ref={(el) => {
                    slotRefs.current[index] = el;
                    if (el && observerRef.current) observerRef.current.observe(el);
                  }}
                  data-page-index={index}
                  style={{ flexShrink: 0 }}
                >
                  {visiblePages.has(index) ? (
                    <CanvasPage
                      pdfDoc={pdfDoc}
                      pageNumber={index + 1}
                      baseScale={baseScale}
                      zoom={zoom}
                      width={targetWidth}
                      height={targetHeight}
                    />
                  ) : (
                    <div
                      style={{
                        width: targetWidth,
                        height: targetHeight,
                        background: '#9ca3af',
                        borderRadius: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <span style={{ color: '#d1d5db', fontSize: '13px' }}>Página {index + 1}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Document>
      </div>

      {transparent && (
        <style>{`
          .pdf-scroll-dark::-webkit-scrollbar { width: 8px; height: 8px; }
          .pdf-scroll-dark::-webkit-scrollbar-track { background: transparent; }
          .pdf-scroll-dark::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 4px; }
          .pdf-scroll-dark::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.4); }
          .pdf-scroll-dark { scrollbar-color: rgba(255,255,255,0.2) transparent; scrollbar-width: thin; }
        `}</style>
      )}
    </div>
  );
});

export default PdfViewer;
